/**
 * DomainContext — T9 Frente 3.1
 *
 * Lifts the active "domain profile" from the Cerberus JWT (T8 claim
 * `domain_profiles[]` + `active_domain`) into React context so that the
 * shell — `ThemeProvider`, `BottomNav`, `FAB`, routers — can render
 * brand/navigation off a single source of truth instead of hardcoding
 * `brand="cuidari"`.
 *
 * Spec: docs/specs/cuidari/t9-platform-generalization.md §5.1 / §5.2.
 *
 * Behaviour:
 *   - When the JWT carries `domain_profiles[]`, pick the active one (by
 *     `claims.active_domain`, or the first profile if no hint) and expose
 *     a fully-populated `DomainProfile`.
 *   - When the JWT is missing (`status !== 'authed'`) OR `domain_profiles`
 *     is empty/absent (T8 not yet populated for this user), fall back to
 *     the canonical Cuidari profile so production behaviour is preserved
 *     while the rest of the wire-up (F3.2-F3.6) lands.
 *
 * Future frentes will:
 *   - F3.2: pull `display_name` / `default_route` / `primary_action_id`
 *     from the module manifest registry rather than this hardcoded
 *     fallback.
 *   - F3.3/3.4: consume `useDomain()` from `BottomNav` / `FAB`.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react'
import type { Brand } from '@francisquiniai/hestia-web'
import { useAuth } from '@/lib/cerberus/useAuth'
import { getRegisteredModules } from '@/lib/modules/registry'
import type { ModuleManifest } from '@/lib/modules/types'

interface DomainProfile {
  /** Domain identifier (e.g. "finance", "health"). */
  domain: string
  /** Public-facing brand display name (e.g. "Cuidari"). */
  display_name: string
  /** Brand key consumed by `ThemeProvider` from @francisquiniai/hestia-web. */
  brand: Brand
  /** Landing route used by the shell's default redirect. */
  default_route: string
  /** Manifest action id rendered by the FAB primary action. */
  primary_action_id: string
}

/**
 * Fallback profile applied when the JWT does not carry domain_profiles.
 * Mirrors the pre-T9 hardcoded behaviour so production stays identical
 * for users whose tokens predate T8's `domain_profiles[]` claim, or who
 * have not yet been seeded with a finance profile.
 */
export const DEFAULT_DOMAIN_PROFILE: DomainProfile = {
  domain: 'finance',
  display_name: 'Cuidari',
  brand: 'cuidari',
  default_route: '/',
  primary_action_id: 'finance.create_bill',
}

const DomainContext = createContext<DomainProfile | null>(null)

/**
 * Resolve the manifest whose `module_id` matches the active domain.
 *
 * Convention (T9 Frente 3 / parity F1): a domain's canonical module
 * declares `module_id === domain` (e.g. domain `'finance'` → manifest at
 * `src/modules/finance/manifest.ts` with `module_id: 'finance'`). When no
 * registered module declares that id we return `undefined` so the
 * provider falls through to `DEFAULT_DOMAIN_PROFILE` for each field.
 */
function findManifestForDomain(domain: string): ModuleManifest | undefined {
  return getRegisteredModules().find((m) => m.module_id === domain)
}

interface DomainProviderProps {
  children: ReactNode
  /**
   * Override the resolved profile. Primarily for tests/Storybook; in
   * production the value is derived from the JWT.
   */
  value?: DomainProfile
}

/**
 * Resolves the active domain profile from the JWT claims and exposes it
 * to descendants via context. Falls back to `DEFAULT_DOMAIN_PROFILE`
 * whenever the JWT cannot provide a populated profile.
 */
export function DomainProvider({ children, value }: DomainProviderProps) {
  const { claims } = useAuth()

  const resolved = useMemo<DomainProfile>(() => {
    if (value) return value

    const profiles = claims?.domain_profiles ?? []
    if (profiles.length === 0) return DEFAULT_DOMAIN_PROFILE

    const activeDomain = claims?.active_domain ?? profiles[0]?.domain
    const match = profiles.find((p) => p.domain === activeDomain)
    if (!match) return DEFAULT_DOMAIN_PROFILE

    // F1 (parity): brand / primary_action_id / default_route are now read
    // from the module manifest whose `module_id` matches the active domain,
    // instead of the previous hardcoded `domain === 'finance' ? 'cuidari'`
    // mapping. New domains/modules just declare `brand` + `primary_action_id`
    // in their manifest.ts and the shell picks them up automatically; the
    // DEFAULT_DOMAIN_PROFILE values stay as the per-field fallback when the
    // manifest omits a field or when no manifest is registered for the
    // active domain.
    const manifest = findManifestForDomain(match.domain)
    const defaultRoute =
      manifest?.nav?.primary?.find((tab) => tab.order <= 10)?.path ??
      manifest?.nav?.primary?.[0]?.path ??
      DEFAULT_DOMAIN_PROFILE.default_route

    return {
      domain: match.domain,
      display_name: match.display_name ?? DEFAULT_DOMAIN_PROFILE.display_name,
      brand: manifest?.brand ?? DEFAULT_DOMAIN_PROFILE.brand,
      default_route: defaultRoute,
      primary_action_id:
        manifest?.primary_action_id ?? DEFAULT_DOMAIN_PROFILE.primary_action_id,
    }
  }, [claims, value])

  return <DomainContext.Provider value={resolved}>{children}</DomainContext.Provider>
}

/**
 * Read the active domain profile. Must be called from inside a
 * `<DomainProvider>` — throws otherwise to surface wire-up mistakes
 * early instead of silently rendering the fallback.
 */
export function useDomain(): DomainProfile {
  const ctx = useContext(DomainContext)
  if (!ctx) {
    throw new Error('useDomain must be used inside <DomainProvider>')
  }
  return ctx
}
